import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Paid Social That Reaches the Right Audiences",
  seoDescription: "Strategic social advertising that connects your brand with qualified audiences across Meta, LinkedIn, and emerging platforms. Full-funnel programs that balance brand building with direct response.",
  canonicalUrl: "https://xperfomax.com/paid-social",
  category: "services",
  categoryLabel: "Services / Paid Social",
  headline: "Reach the right audiences with precision.",
  subheadline: "Strategic social advertising that connects your brand with qualified audiences across Meta, LinkedIn, and emerging platforms.",
  heroSecondaryText: "We build full-funnel programs that balance brand building with direct response.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why most companies struggle with paid social.",
  problems: [
    "Platform complexity without strategy connecting features to outcomes.",
    "Vanity metrics obsession. Optimizing reach instead of acquisition costs.",
    "Creative fatigue accelerates faster than teams can iterate.",
    "Privacy signal loss degrades targeting from platform restrictions.",
  ],
  systemTitle: "Our Paid Social System",
  systemDescription: "Audience intelligence, funnel-based planning, creative testing, and measurement that adapts to privacy changes.",
  services: [
    { name: "Full-Funnel Campaign Architecture", icon: "Layers", components: ["Awareness to conversion structure", "Stage-appropriate messaging", "Sequential audience journeys"], outcome: "Campaigns that move audiences through the funnel." },
    { name: "Audience Building and Segmentation", icon: "Users", components: ["First-party data activation", "Lookalike modeling", "Behavioral targeting"], outcome: "Precision targeting that reaches the right people." },
    { name: "Creative Testing Methodology", icon: "Palette", components: ["Systematic variable testing", "Creative element isolation", "Winner scaling frameworks"], outcome: "Data-driven creative decisions that compound." },
    { name: "Conversion API Integration", icon: "Zap", components: ["Server-side tracking", "Signal quality maintenance", "Privacy-compliant measurement"], outcome: "Accurate measurement despite browser restrictions." },
    { name: "Cross-Platform Coordination", icon: "Share2", components: ["Meta, LinkedIn, TikTok management", "Unified strategy across platforms", "Consistent measurement"], outcome: "Platform-specific execution with unified strategy." },
    { name: "Performance Creative Strategy", icon: "Target", components: ["Brand-performance balance", "Direct response optimization", "Asset development guidance"], outcome: "Creative that builds brand while driving action." },
  ],
  systemShift: {
    title: "The social shift.",
    beforeTitle: "Before: Random Social Spending",
    afterTitle: "After: Systematic Audience Building",
    items: [
      { label: "Audience Strategy", before: "Interest-based targeting with limited precision", after: "First-party data and lookalike modeling at scale" },
      { label: "Creative Approach", before: "Random creative testing without methodology", after: "Systematic testing that isolates winning elements" },
      { label: "Funnel Design", before: "Direct response campaigns without brand building", after: "Full-funnel architecture balancing awareness and conversion" },
      { label: "Measurement", before: "Vanity metrics and platform-reported attribution", after: "Server-side tracking with accurate conversion data" },
      { label: "Platform Management", before: "Siloed campaigns across platforms", after: "Unified strategy with platform-specific execution" },
    ],
  },
  engagementShape: {
    title: "How paid social engagement unfolds.",
    description: "No timelines. No deliverables lists. Structure that adapts to your creative and audience context.",
  },
  processTitle: "From discovery to scale.",
  processSteps: [
    { number: "01", title: "Audit and Discovery", description: "Deep analysis of current social advertising performance, audience composition, and competitive positioning." },
    { number: "02", title: "Audience Strategy", description: "Building detailed audience frameworks based on behavior, intent signals, and customer data integration." },
    { number: "03", title: "Creative Framework", description: "Developing testing methodologies and creative guidelines that align with platform best practices and brand standards." },
    { number: "04", title: "Campaign Execution", description: "Structured campaign launches with proper tracking, attribution, and measurement foundations in place." },
    { number: "05", title: "Optimize and Scale", description: "Continuous refinement through systematic testing, audience expansion, and budget reallocation based on performance data." },
  ],
  technologyCategories: [
    { title: "Platforms", icon: "Megaphone", items: ["Meta Ads Manager", "LinkedIn Campaign Manager", "TikTok Ads", "Pinterest Ads"] },
    { title: "Analytics", icon: "LineChart", items: ["Google Analytics 4", "Segment", "Triple Whale"] },
    { title: "Creative", icon: "Palette", items: ["Motion", "Foreplay", "Creative Testing Tools"] },
    { title: "Attribution", icon: "BarChart3", items: ["Conversion APIs", "Server-Side Tracking", "MMM"] },
  ],
  techTitle: "Paid Social Tools",
  techDescription: "Platform expertise matters, but strategy determines results.",
  qualification: {
    forYou: [
      "You have defined audiences and clear value propositions",
      "You're ready to invest in systematic testing and optimization",
      "You have creative resources or willingness to develop them",
      "You're seeking to build both brand and direct response capabilities",
    ],
    notForYou: [
      "You expect immediate results without creative investment",
      "You're not willing to test and iterate on messaging",
      "Your product or service does not have clear audience segments",
      "You want to run campaigns without proper tracking infrastructure",
    ],
  },
  faqs: [
    { question: "What budget level is needed for effective paid social campaigns?", answer: "Effective budget levels depend on your goals, target audience size, and competitive landscape. We assess feasibility during discovery and recommend investment levels that can generate meaningful data and results." },
    { question: "How do you approach creative development?", answer: "We establish testing frameworks and provide creative direction based on platform best practices and performance data. We work with your internal team or creative partners to develop assets that align with both brand standards and performance objectives." },
    { question: "How quickly will we see results from paid social?", answer: "Initial performance signals typically emerge within the first few weeks. Building optimized campaigns with efficient unit economics generally requires two to four months of systematic testing and refinement." },
    { question: "How do you handle measurement with increasing privacy restrictions?", answer: "We implement Conversion APIs and server-side tracking to maintain signal quality. We also develop measurement frameworks that account for attribution gaps and provide accurate views of campaign contribution." },
    { question: "Which platforms do you recommend?", answer: "Platform selection depends on your audience, objectives, and creative capabilities. We recommend platforms based on where your customers spend time and where you can achieve efficient reach. Meta and LinkedIn are common starting points, with expansion based on performance." },
    { question: "How does paid social integrate with other channels?", answer: "Paid social works best as part of a connected marketing system. We coordinate with content strategy for social proof, with CRO for landing page optimization, and with analytics for unified measurement across touchpoints." },
  ],
  finalCtaTitle: "Build audiences that convert.",
  finalCtaDescription: "Social advertising works when strategy, creative, and measurement work together.",
  finalCtaButtonText: "Start a serious conversation",
};

const PaidSocial = () => {
  const { content } = usePageContent("paid-social", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      heroSecondaryText={content.heroSecondaryText}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      systemShift={content.systemShift}
      engagementShape={content.engagementShape}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default PaidSocial;
